import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Activity, Search, Plus, Eye, FileText, RefreshCw, X } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const PTDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', documentType: 'PT Evaluation',
    visitDate: new Date().toISOString().split('T')[0], notes: '', status: 'Draft'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, patientsRes] = await Promise.all([
        api.get('/visit-documents/type/PT'),
        api.get('/patients')
      ]);
      if (docsRes.data.success) setDocuments(docsRes.data.documents || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName) { toast.error('Please select a patient'); return; }
    try {
      const res = await api.post('/visit-documents', { ...formData, discipline: 'PT' });
      if (res.data.success) {
        toast.success('Document created');
        setDocuments(prev => [...prev, res.data.document]);
        setShowModal(false);
        setFormData({ patientId: '', patientName: '', documentType: 'PT Evaluation', visitDate: new Date().toISOString().split('T')[0], notes: '', status: 'Draft' });
      }
    } catch (error) {
      toast.error('Failed to create document');
    }
  };

  const filteredDocs = documents.filter(d =>
    d.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.documentType?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: documents.length,
    completed: documents.filter(d => d.status === 'Completed').length,
    pending: documents.filter(d => d.status === 'Draft' || d.status === 'In Progress').length
  };

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="pt-documents-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Activity className="w-6 h-6 text-blue-600" />
                Physical Therapy Documents
              </h1>
              <p className="text-gray-500 text-sm mt-1">Manage PT evaluations, visit notes, and progress reports</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />New PT Document
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <div className="text-sm text-blue-600">Total Documents</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <div className="text-sm text-yellow-600">Pending</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search documents..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No PT documents found. Click "New PT Document" to create one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Document Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Visit Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredDocs.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{doc.patientName}</td>
                    <td className="px-6 py-4">{doc.documentType}</td>
                    <td className="px-6 py-4">{doc.visitDate}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${doc.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Button size="sm" variant="outline"><Eye className="w-4 h-4 mr-1" />View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">New PT Document</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select value={formData.patientId} onChange={(e) => {
                    const p = patients.find(p => p.id === e.target.value);
                    setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                  }} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Document Type</label>
                  <select value={formData.documentType} onChange={(e) => setFormData(prev => ({ ...prev, documentType: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                    <option>PT Evaluation</option>
                    <option>PT Visit Note</option>
                    <option>PT Progress Report</option>
                    <option>PT Discharge Summary</option>
                    <option>PT Re-Evaluation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Visit Date</label>
                  <Input type="date" value={formData.visitDate} onChange={(e) => setFormData(prev => ({ ...prev, visitDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={3} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit}>Create</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PTDocumentsPage;
